import { createSlice, createSelector } from '@reduxjs/toolkit'
import {
    dashboardDataRequestThunk,
    dashboardPeriodicDataRequestThunk,
    dashboardOptionsRequestThunk,
    dashboardFiltersRequestThunk,
} from './dashboardThunks'
import { areFilterValuesEqual } from '../../utils/filterValueUtils'
import { buildPanelRequestKey } from './panelRequestUtils'

const initialPanelState = {
    config: null,
    data: null,
    loading: false,
    error: '',
    httpStatusCode: null,
    errorDetail: '',
    errorRequestId: null,
    pendingRequestKey: null,
    latestRequestId: null,
}

const initialDashboardItemState = {
    id: null,
    name: '',
    subtitle: '',
    description: '',
    owner: null,
    panels: {},
    filters: {
        loading: false,
        error: '',
        config: [],
        currentValues: {}
    },
    panelCameras: {}
}

const initialFiltersState = {
    loading: false,
    error: '',
    config: [],
    currentValues: {}
}

const initialDashboardState = {
    dashboards: {},
    loading: false,
    error: '',
    currentDashboardId: null,
    initialized: false,
    applyFiltersVersion: 0,
    latestOptionsRequestId: null,
}

export const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState: initialDashboardState,
    reducers: {
        resetDashboard: (state) => {
            state.dashboards = {}
            state.currentDashboardId = null
        },
        setCurrentDashboard: (state, action) => {
            state.currentDashboardId = action.payload
        },
        setFilterValues: (state, action) => {
            const { dashboardId, values } = action.payload;
            if (state.dashboards[dashboardId]) {
                if (values && typeof values === 'object' && !Array.isArray(values)) {
                    const current = state.dashboards[dashboardId].filters.currentValues;
                    if (areFilterValuesEqual(current, values)) return;
                    state.dashboards[dashboardId].filters.currentValues = values;
                }
            }
        },
        setPanelCamera: (state, action) => {
            const { dashboardId, panelId, cameraId } = action.payload;
            if (state.dashboards[dashboardId]) {
                state.dashboards[dashboardId].panelCameras[panelId] = cameraId;
            }
        },
        setInitialized: (state, action) => {
            state.initialized = action.payload;
        },
        incrementApplyFiltersVersion: (state) => {
            state.applyFiltersVersion += 1;
        }
    },
    extraReducers: (builder) => {
        // Dashboard configuration and filters belong to the authenticated user.
        // Use action types to avoid the user/application/dashboard import cycle.
        builder.addCase('user/logout/pending', () => initialDashboardState)
        builder.addCase('user/tokenRequest/pending', () => initialDashboardState)
        builder.addCase('user/tokenRequest/fulfilled', () => initialDashboardState)
        builder.addCase(dashboardOptionsRequestThunk.pending, (state, action) => {
            state.latestOptionsRequestId = action.meta.requestId;
            state.loading = true;
            state.error = '';
        })
        builder.addCase(dashboardOptionsRequestThunk.fulfilled, (state, action) => {
            if (state.latestOptionsRequestId !== action.meta.requestId) return;
            state.latestOptionsRequestId = null;
            state.loading = false;
            state.error = '';
            action.payload.forEach(dashboard => {
                const dashboardId = dashboard.id;
                if (dashboardId) {
                    const existingDashboard = state.dashboards[dashboardId];
                    const existingPanels = existingDashboard?.panels || {};
                    const nextPanels = {};
                    const panelsConfigArray = dashboard.data || [];

                    panelsConfigArray.forEach(panelConfig => {
                        const panelKey = panelConfig.unique_id || panelConfig.id;
                        if (panelKey) {
                            nextPanels[panelKey] = {
                                ...initialPanelState,
                                ...(existingPanels[panelKey] || {}),
                                config: panelConfig
                            };
                        }
                    });

                    state.dashboards[dashboardId] = {
                        ...initialDashboardItemState,
                        ...(existingDashboard || {}),
                        id: dashboard.id,
                        name: dashboard.name,
                        subtitle: dashboard.subtitle,
                        description: dashboard.description,
                        owner: dashboard.owner,
                        panels: nextPanels,
                        filters: existingDashboard?.filters || { ...initialFiltersState }
                    };
                }
            });
        })
        builder.addCase(dashboardOptionsRequestThunk.rejected, (state, action) => {
            if (state.latestOptionsRequestId !== action.meta.requestId) return;
            state.latestOptionsRequestId = null;
            state.loading = false;
            state.error = action.error?.message || 'Failed to load dashboards';
        })
        builder.addCase(dashboardDataRequestThunk.pending, (state, action) => {
            const panel = action.meta.arg?.panel;
            const panelKey = panel?.unique_id || panel?.id;
            const dashboardId = action.meta.arg?.dashboardId || state.currentDashboardId;
            if (panelKey && dashboardId && state.dashboards[dashboardId]) {
                if (!state.dashboards[dashboardId].panels[panelKey]) {
                    state.dashboards[dashboardId].panels[panelKey] = { ...initialPanelState };
                }
                state.dashboards[dashboardId].panels[panelKey].loading = true;
                state.dashboards[dashboardId].panels[panelKey].error = '';
                state.dashboards[dashboardId].panels[panelKey].httpStatusCode = null;
                state.dashboards[dashboardId].panels[panelKey].errorDetail = '';
                state.dashboards[dashboardId].panels[panelKey].errorRequestId = null;
                state.dashboards[dashboardId].panels[panelKey].latestRequestId = action.meta.requestId;
                state.dashboards[dashboardId].panels[panelKey].pendingRequestKey = buildPanelRequestKey({
                    dashboardId,
                    panel,
                    filterConfig: action.meta.arg?.filterConfig,
                    filterValues: action.meta.arg?.filterValues,
                    extra: action.meta.arg?.extra,
                });
            }
        })
        builder.addCase(dashboardDataRequestThunk.fulfilled, (state, action) => {
            const panel = action.meta.arg?.panel;
            const panelKey = panel?.unique_id || panel?.id;
            const dashboardId = action.meta.arg?.dashboardId || state.currentDashboardId;
            if (panelKey && dashboardId && state.dashboards[dashboardId]) {
                if (!state.dashboards[dashboardId].panels[panelKey]) {
                    state.dashboards[dashboardId].panels[panelKey] = { ...initialPanelState };
                }
                const panelState = state.dashboards[dashboardId].panels[panelKey];
                if (panelState.latestRequestId !== action.meta.requestId) return;
                panelState.data = action.payload;
                panelState.loading = false;
                panelState.error = '';
                panelState.httpStatusCode = null;
                panelState.errorDetail = '';
                panelState.errorRequestId = null;
                panelState.pendingRequestKey = null;
            }
        })
        builder.addCase(dashboardDataRequestThunk.rejected, (state, action) => {
            const panel = action.meta.arg?.panel;
            const panelKey = panel?.unique_id || panel?.id;
            const dashboardId = action.meta.arg?.dashboardId || state.currentDashboardId;
            if (panelKey && dashboardId && state.dashboards[dashboardId]) {
                if (!state.dashboards[dashboardId].panels[panelKey]) {
                    state.dashboards[dashboardId].panels[panelKey] = { ...initialPanelState };
                }
                const panelState = state.dashboards[dashboardId].panels[panelKey];
                if (panelState.latestRequestId !== action.meta.requestId) return;
                panelState.loading = false;
                panelState.error = 'Rejected';
                panelState.httpStatusCode = action.payload?.http_status_code || null;
                panelState.errorDetail = action.payload?.detail || '';
                panelState.errorRequestId = action.payload?.request_id || null;
                panelState.pendingRequestKey = null;
            }
        })

        builder.addCase(dashboardPeriodicDataRequestThunk.fulfilled, (state, action) => {
            const dashboardId = action.payload?.dashboardId || state.currentDashboardId;
            const results = action.payload?.results || {};
            const errors = action.payload?.errors || {};
            if (!dashboardId || !state.dashboards[dashboardId]) return;

            Object.entries(results).forEach(([panelId, data]) => {
                if (!state.dashboards[dashboardId].panels[panelId]) {
                    state.dashboards[dashboardId].panels[panelId] = { ...initialPanelState };
                }
                state.dashboards[dashboardId].panels[panelId].data = data;
                state.dashboards[dashboardId].panels[panelId].error = '';
                state.dashboards[dashboardId].panels[panelId].httpStatusCode = null;
                state.dashboards[dashboardId].panels[panelId].errorDetail = '';
                state.dashboards[dashboardId].panels[panelId].errorRequestId = null;
            });

            Object.entries(errors).forEach(([panelId, err]) => {
                if (!state.dashboards[dashboardId].panels[panelId]) {
                    state.dashboards[dashboardId].panels[panelId] = { ...initialPanelState };
                }
                state.dashboards[dashboardId].panels[panelId].error = 'Rejected';
                state.dashboards[dashboardId].panels[panelId].httpStatusCode = err?.http_status_code || null;
                state.dashboards[dashboardId].panels[panelId].errorDetail = err?.detail || '';
                state.dashboards[dashboardId].panels[panelId].errorRequestId = err?.request_id || null;
            });
        })

        builder.addCase(dashboardFiltersRequestThunk.pending, (state, action) => {
            const dashboardId = action.meta.arg?.dashboardId || state.currentDashboardId;
            if (dashboardId && !state.dashboards[dashboardId]) {
                state.dashboards[dashboardId] = {
                    ...initialDashboardItemState,
                    id: dashboardId,
                };
            }
            if (dashboardId) {
                state.dashboards[dashboardId].filters.loading = true;
                state.dashboards[dashboardId].filters.latestRequestId = action.meta.requestId;
                state.dashboards[dashboardId].filters.error = '';
            }
        })
        builder.addCase(dashboardFiltersRequestThunk.fulfilled, (state, action) => {
            const dashboardId = action.meta.arg?.dashboardId || state.currentDashboardId;
            if (state.dashboards[dashboardId]?.filters.latestRequestId !== action.meta.requestId) return;
            state.dashboards[dashboardId].filters.latestRequestId = null;
            if (dashboardId && !state.dashboards[dashboardId]) {
                state.dashboards[dashboardId] = {
                    ...initialDashboardItemState,
                    id: dashboardId,
                };
            }
            if (dashboardId) {
                state.dashboards[dashboardId].filters.loading = false;
                state.dashboards[dashboardId].filters.config = action.payload;
            }
        })
        builder.addCase(dashboardFiltersRequestThunk.rejected, (state, action) => {
            const dashboardId = action.meta.arg?.dashboardId || state.currentDashboardId;
            if (state.dashboards[dashboardId]?.filters.latestRequestId !== action.meta.requestId) return;
            state.dashboards[dashboardId].filters.latestRequestId = null;
            if (dashboardId && !state.dashboards[dashboardId]) {
                state.dashboards[dashboardId] = {
                    ...initialDashboardItemState,
                    id: dashboardId,
                };
            }
            if (dashboardId) {
                state.dashboards[dashboardId].filters.loading = false;
                state.dashboards[dashboardId].filters.error = action.payload?.http_status_code || 'server_error';
            }
        })
    }
})

export const { resetDashboard, setCurrentDashboard, setFilterValues, setPanelCamera, setInitialized, incrementApplyFiltersVersion } = dashboardSlice.actions

export const selectDashboards = (state) => state.dashboard.dashboards;
export const selectCurrentDashboardId = (state) => state.dashboard.currentDashboardId;
export const selectDashboardLoading = (state) => state.dashboard.loading;
export const selectDashboardError = (state) => state.dashboard.error;
export const selectDashboardInitialized = (state) => state.dashboard.initialized;
export const selectApplyFiltersVersion = (state) => state.dashboard.applyFiltersVersion;

export const selectCurrentDashboard = createSelector(
    [selectCurrentDashboardId, selectDashboards],
    (currentDashboardId, dashboards) => 
        currentDashboardId ? dashboards[currentDashboardId] : null
);

export const selectPanelByPanelIdDashboardId = createSelector(
    [selectDashboards, (state, dashboardId) => dashboardId, (state, dashboardId, panelId) => panelId],
    (dashboards, dashboardId, panelId) => 
        dashboards[dashboardId]?.panels?.[panelId] || null
);

export const selectPanelsByDashboardId = createSelector(
    [selectCurrentDashboard],
    (currentDashboard) => currentDashboard?.panels || {}
);

export const selectFilterConfig = createSelector(
    [selectCurrentDashboard],
    (currentDashboard) => currentDashboard?.filters?.config || []
);

export const selectFilterValues = createSelector(
    [selectCurrentDashboard],
    (currentDashboard) => currentDashboard?.filters?.currentValues || {}
);

export const selectPanelCameras = createSelector(
    [selectCurrentDashboard],
    (currentDashboard) => currentDashboard?.panelCameras || {}
);

export default dashboardSlice.reducer
